import { bimKernel } from '../core/BIMKernel';
import { useBIMStore } from '../state/useBIMStore';
import { useDocumentStore, DocumentKind, type RecentFile } from '../state/useDocumentStore';
import { useAppStore } from '../state/store';
import { useDocSession, type SessionDocument, type SessionSnapshot } from '../workspaces/sessionStore';
import { useBlockStore, useViewStore } from '../state/commandAuxStores';
import { useLayoutStore } from '../state/useLayoutStore';
import { useSketchStore } from '../state/useSketchStore';
import { isSceneObject, sceneObjectToCadEntity, type SceneObject } from '../core/scene';
import type { SketchSolid, SketchGuide, SketchGroup, SketchComponentDef, SketchLooseEdge, SketchTag, SketchScene, SketchShadowSettings } from '../core/sketch/types';
import { buildProjectJsonFromTemplate, getCustomTemplateJson } from '../core/ProjectTemplateManager';
import { BUILTIN_PROJECT_TEMPLATES } from '../config/projectTemplates';
import {
    downloadTextFile,
    isFileSystemAccessSupported,
    pickFileToOpen,
    pickFileToSave,
    PROJECT_FILE_EXTENSION,
    readHandleText,
    stripExtension,
    TEMPLATE_FILE_EXTENSION,
    writeHandleText,
} from '../services/fileSystem/fileAccess';
import { getRecentHandle, verifyReadPermission } from '../services/fileSystem/recentHandleStore';
import { externalizeAssets, resolveAssetRefs } from '../services/fileSystem/assetSidecar';
import * as virtualFs from '../services/fileSystem/virtualFs';
import { notify } from './notify';
import { useUnsavedChangesStore } from '../state/useUnsavedChangesStore';
import { clearSnapshot, writeSnapshot } from './autosave';
import i18n from '../i18n';

/**
 * Project File I/O
 *
 * Orchestrates every document-level file operation (New / Open / Save /
 * Save As / Template export) on top of the BIM kernel, the document store
 * and the file access gateway.
 *
 * On-disk format — the .yap/.yate envelope (project/template file pair):
 * {
 *   format: 'yap-project' | 'yap-template',
 *   version: 1,
 *   appName: 'Yap',
 *   savedAt: ISO string,
 *   project: { ...serialized kernel project... }
 * }
 * Legacy raw kernel JSON files are still readable (treated as projects).
 */

export type YapFileFormat = 'yap-project' | 'yap-template';

/**
 * Non-BIM scene content, kept in the shared SceneObject protocol so every
 * workbench (CAD today, NURBS later) round-trips inside one document.
 * BIM elements are NOT stored here — they live in `project.elements` as the
 * kernel's own serialization and are mirrored into the ledger at load time.
 */
export interface CadScenePayload {
    objects: SceneObject[];
    /** Document-scoped CAD side state: layers, block definitions, named
     *  views (docs/plans/2d plan Faz 6.2) — round-trips with the file. */
    layers?: unknown[];
    activeLayerId?: string;
    blocks?: unknown[];
    namedViews?: unknown[];
    layouts?: unknown;
}

export interface YapFileEnvelope {
    format: YapFileFormat;
    version: 1;
    appName: 'Yap';
    savedAt: string;
    project: Record<string, unknown>;
    /** Shared-protocol scene content beyond the BIM kernel model. */
    cadScene?: CadScenePayload;
    /** Sketch workbench content: analytic solids + guides (core/sketch). */
    sketchScene?: SketchScenePayload;
}

export interface SketchScenePayload {
    solids: SketchSolid[];
    edges?: SketchLooseEdge[];
    guides: SketchGuide[];
    groups?: SketchGroup[];
    componentDefs?: SketchComponentDef[];
    tags?: SketchTag[];
    scenes?: SketchScene[];
    shadows?: SketchShadowSettings;
}

/** Collect the current Sketch document (solids + guides). */
function currentSketchScene(): SketchScenePayload | undefined {
    const sketch = useSketchStore.getState().sketchDocumentSnapshot();
    if (sketch.solids.length === 0 && sketch.guides.length === 0) return undefined;
    return sketch;
}

/** Collect the current CAD ledger records + document-scoped CAD state. */
function currentCadScene(): CadScenePayload | undefined {
    const ledger = useDocumentStore.getState().sceneObjects;
    const objects = Object.values(ledger).filter((o) => o.source === 'cad');
    const { layers, activeLayerId } = useAppStore.getState();
    const blockDefs = Object.values(useBlockStore.getState().defs);
    const { namedViews } = useViewStore.getState();
    const layoutState = useLayoutStore.getState().serialize();
    const side = {
        layers: Object.values(layers),
        activeLayerId,
        blocks: blockDefs,
        namedViews,
        layouts: layoutState,
    };
    if (objects.length === 0 && blockDefs.length === 0 && Object.keys(layers).length <= 1 && namedViews.length === 0
        && layoutState.layouts.length === 0) {
        return undefined;
    }
    return { objects, ...side };
}

export function buildEnvelope(kind: DocumentKind, projectJson: string): YapFileEnvelope {
    return {
        format: kind === 'template' ? 'yap-template' : 'yap-project',
        version: 1,
        appName: 'Yap',
        savedAt: new Date().toISOString(),
        project: JSON.parse(projectJson),
        cadScene: currentCadScene(),
        sketchScene: currentSketchScene(),
    };
}

/**
 * Parses file contents into a loadable document payload. Accepts the
 * current envelope format plus legacy raw kernel project JSON; returns
 * null for anything else (e.g. the old CAD-store .yap files).
 */
export function parseDocument(text: string): ParsedDocument | null {
    let parsed: any;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    return parseDocumentObject(parsed);
}

export interface ParsedDocument {
    kind: DocumentKind;
    /** The kernel project object (maps as entry arrays) — feeds
        loadProjectData directly, with no re-stringify/re-parse. */
    project: Record<string, unknown>;
    cadScene?: CadScenePayload;
    sketchScene?: SketchScenePayload;
}

function parseDocumentObject(parsed: any): ParsedDocument | null {
    if (!parsed || typeof parsed !== 'object') return null;

    if (parsed.format === 'yap-project' || parsed.format === 'yap-template') {
        if (!parsed.project || !Array.isArray(parsed.project.elements)) return null;
        const cadScene = extractCadScene(parsed.cadScene);
        const sketchScene = extractSketchScene(parsed.sketchScene);
        return { kind: parsed.format === 'yap-template' ? 'template' : 'project', project: parsed.project, cadScene, sketchScene };
    }

    // Legacy pre-envelope saves (raw serialized kernel project).
    if (Array.isArray(parsed.elements) && Array.isArray(parsed.views) && Array.isArray(parsed.levels)) {
        return { kind: 'project', project: parsed };
    }
    return null;
}

/** Validate envelope sketchScene records (structural parity, not strict). */
function extractSketchScene(raw: unknown): SketchScenePayload | undefined {
    const payload = raw as SketchScenePayload | undefined;
    if (!payload || !Array.isArray(payload.solids)) return undefined;
    const solids = payload.solids.filter((s) => s && typeof s.id === 'string' && Array.isArray(s.faces));
    const guides = Array.isArray(payload.guides) ? payload.guides.filter(Boolean) : [];
    const groups = Array.isArray(payload.groups) ? payload.groups.filter(Boolean) : [];
    const componentDefs = Array.isArray(payload.componentDefs) ? payload.componentDefs.filter(Boolean) : [];
    if (solids.length === 0 && guides.length === 0 && groups.length === 0 && componentDefs.length === 0) return undefined;
    return { solids, guides, groups, componentDefs };
}

/** Validate envelope cadScene records against the shared protocol. */
function extractCadScene(raw: unknown): CadScenePayload | undefined {
    const objects = Array.isArray((raw as CadScenePayload | undefined)?.objects)
        ? (raw as CadScenePayload).objects
        : undefined;
    if (!objects || objects.length === 0) return undefined;
    const valid = objects.filter(isSceneObject);
    return valid.length > 0 ? { objects: valid } : undefined;
}


async function serializeDocument(kind: DocumentKind): Promise<string> {
    const projectJson = await bimKernel.saveProject();
    const envelope = buildEnvelope(kind, projectJson);
    // Park large binary payloads (data URLs) in the asset side-store and
    // write compact tokens instead, so repeated saves stop shipping the
    // full base64 of every embedded raster over and over.
    await externalizeAssets(envelope);
    return JSON.stringify(envelope);
}

/**
 * In-memory session snapshot for tab parking: the live kernel project +
 * scene payloads wrapped in the envelope shape — NO serialization at all.
 * Object references are shared by invariant (see SessionSnapshot): a
 * background document's kernel/stores are replaced wholesale before anything
 * can mutate them, so the parked graph stays frozen until the document is
 * reactivated (which wipes the snapshot via syncActive).
 */
function buildSessionSnapshot(kind: DocumentKind): SessionSnapshot {
    return {
        format: kind === 'template' ? 'yap-template' : 'yap-project',
        version: 1,
        appName: 'Yap',
        savedAt: new Date().toISOString(),
        project: bimKernel.snapshotProject(),
        cadScene: currentCadScene(),
        sketchScene: currentSketchScene(),
    };
}

// --- Autosave snapshot ---

/**
 * Captures the current document as an autosave recovery snapshot. Injected
 * into the autosave engine by the App bootstrap; silently no-ops when the
 * document is not (or no longer) dirty so a snapshot never races a save.
 */
export async function captureAutosaveSnapshot(): Promise<void> {
    const doc = useDocumentStore.getState();
    if (!doc.isDirty) return;
    try {
        const envelopeText = await serializeDocument(doc.kind);
        writeSnapshot({
            displayName: doc.displayName,
            kind: doc.kind,
            savedAt: new Date().toISOString(),
            envelopeText,
        });
    } catch (error) {
        console.error('[ProjectFileIO] Autosave snapshot failed:', error);
    }
}

/**
 * Restores the working document from a crash-recovery snapshot. Shares the
 * load pipeline, then drops the obsolete snapshot (the restored document is
 * still dirty-by-origin, so the dirty-transition clear cannot fire here).
 */
export async function restoreFromSnapshot(envelopeText: string, displayName: string): Promise<boolean> {
    const ok = await loadDocumentFromData(envelopeText, displayName, null, null, true);
    if (!ok) return false;
    clearSnapshot();
    // The recovered work was never saved to a real destination: keep the
    // dirty flag so the save prompt and title asterisk stay truthful.
    useDocumentStore.getState().markDirty();
    return true;
}

// --- Unsaved changes guard ---

/**
 * BIM-style "Before you cross out a bunch of work…" gate: whenever a
 * document flow is about to replace the working document, ask first if the
 * document is dirty. Resolves true when the flow may proceed (saved or
 * explicitly discarded), false when the user cancelled.
 */
export async function guardUnsavedChanges(): Promise<boolean> {
    if (!useDocumentStore.getState().isDirty) return true;
    return useUnsavedChangesStore.getState().request();
}

// --- Multi-tab document parking (session snapshots) ---

/**
 * Parks the active document's live state into its session tab just before
 * the kernel is handed to another document (New / Open / tab activation).
 * The parked in-memory envelope is the freshest reopen path for tabs that
 * left the active slot dirty or without a usable file destination. A clean,
 * disk-resolvable document skips the snapshot: the file itself is the truth
 * and the recents pipeline suffices.
 */
export async function parkActiveDocumentSnapshot(): Promise<void> {
    const session = useDocSession.getState();
    const activeKey = session.activeKey;
    const entry = activeKey ? session.docs[activeKey] : undefined;
    if (!entry) return;
    const doc = useDocumentStore.getState();
    const resolvable = !!(entry.recent && (entry.recent.vfsFileId || entry.recent.hasHandle));
    if (!doc.isDirty && resolvable) return;
    try {
        const snapshot = buildSessionSnapshot(doc.kind);
        useDocSession.getState().park(entry.key, snapshot, doc.isDirty);
    } catch (error) {
        console.error('[ProjectFileIO] Failed to park document snapshot:', error);
    }
}

/**
 * Runs before the working document is replaced by a New / Open flow. Single
 * Project Mode keeps the classic save/discard guard — one project at a time
 * means the outgoing document really goes away. Multi-tab mode parks the
 * live document into its session tab instead: nothing is lost, so no
 * prompt — the new document simply opens as another tab.
 */
async function beforeDocumentSwap(): Promise<boolean> {
    if (useAppStore.getState().singleProjectMode) return guardUnsavedChanges();
    await parkActiveDocumentSnapshot();
    return true;
}

/**
 * Reopens a background session tab in the workbench. A parked snapshot is
 * the freshest state (the tab left the active slot with unsaved or
 * unresolvable content); otherwise the document's file destination is
 * re-read best-effort. Tab switching must ALWAYS activate the tab: when
 * nothing restorable exists (never saved, no snapshot, file gone) the tab
 * rebuilds as a fresh document under its own name — an OS file picker is
 * never acceptable from a tab click. The unsaved-changes guard is skipped
 * on purpose: the outgoing document was parked by the caller before this
 * swap, so there is nothing to lose.
 */
export async function restoreSessionDocument(entry: SessionDocument): Promise<boolean> {
    if (entry.snapshot) {
        let fileHandle: FileSystemFileHandle | null = null;
        if (entry.recent?.hasHandle && entry.recent.id) {
            try {
                const handle = await getRecentHandle(entry.recent.id);
                if (handle && (await verifyReadPermission(handle))) fileHandle = handle;
            } catch {
                // Handle store unavailable: the snapshot content itself is
                // the truth — reopen without the file binding.
            }
        }
        const ok = await loadSessionSnapshot(entry.snapshot, entry, fileHandle);
        // A parked dirty document stays dirty after its snapshot is restored
        // — the dirty dot and save prompts must stay truthful.
        if (ok && entry.snapshotDirty) useDocumentStore.getState().markDirty();
        return ok;
    }
    // Disk destinations are best-effort here: unlike the recents menu, a tab
    // activation must never fall through to the OS Open dialog.
    if (entry.recent?.vfsFileId) {
        try {
            const vfsEntry = await virtualFs.getEntry(entry.recent.vfsFileId);
            if (vfsEntry && vfsEntry.type === 'file' && vfsEntry.content !== undefined) {
                return loadDocumentFromData(vfsEntry.content, entry.name, vfsEntry.id, null, true);
            }
        } catch (error) {
            console.error('[ProjectFileIO] Session tab VFS reopen failed:', error);
        }
    } else if (entry.recent?.hasHandle && entry.recent.id) {
        try {
            const handle = await getRecentHandle(entry.recent.id);
            if (handle && (await verifyReadPermission(handle))) {
                const text = await readHandleText(handle);
                return loadDocumentFromData(text, entry.name, null, handle, true);
            }
        } catch (error) {
            console.error('[ProjectFileIO] Session tab file reopen failed:', error);
        }
    }

    // Nothing restorable (never saved, no snapshot, file gone) — the tab
    // still switches: rebuild it as a fresh document under its own name so
    // tab switching always works no matter what happened to the prior state.
    try {
        let freshJson: string;
        if (BUILTIN_PROJECT_TEMPLATES.length > 0) {
            freshJson = await buildProjectJsonFromTemplate(BUILTIN_PROJECT_TEMPLATES[0].id, entry.name);
        } else {
            bimKernel.newDocument(entry.name);
            freshJson = await bimKernel.saveProject();
        }
        await useBIMStore.getState().loadProject(freshJson);
        useBIMStore.getState().clearSelection();
        rehydrateSessionScene(null);
        useSketchStore.getState().clearDocument();
        const doc = useDocumentStore.getState();
        doc.setDocument({ kind: entry.kind, displayName: entry.name });
        doc.pushRecent({ name: entry.name, kind: entry.kind });
        notify(i18n.t('tools.notify.sessionTabRebuiltFresh', { name: entry.name }), 'info');
        return true;
    } catch (error) {
        console.error('[ProjectFileIO] Fresh session tab rebuild failed:', error);
    notify(i18n.t('tools.notify.sessionTabUnavailable', { name: entry.name }), 'warning');
    return false;
}

/**
 * Restore fast path for parked session snapshots. Object snapshots (the
 * current park format) feed the pipeline with ZERO stringify/parse; legacy
 * string snapshots from an older session build parse once, and token-bearing
 * ones go through the full loader for sidecar resolution.
 */
async function loadSessionSnapshot(
    snapshot: SessionSnapshot | string,
    entry: SessionDocument,
    fileHandle: FileSystemFileHandle | null
): Promise<boolean> {
    const loadFull = () =>
        loadDocumentFromData(
            snapshot as string, entry.name, entry.recent?.vfsFileId ?? null, fileHandle, true
        );
    const apply = (parsed: ParsedDocument | null) =>
        parsed
            ? applyDocumentPayload(parsed, entry.name, entry.recent?.vfsFileId ?? null, fileHandle, true)
            : loadFull();

    if (typeof snapshot === 'object') {
        return apply(parseDocumentObject(snapshot));
    }
    // Legacy string snapshot
    if (snapshot.includes('yap-asset:')) return loadFull();
    let parsed: ParsedDocument | null;
    try {
        parsed = parseDocumentObject(JSON.parse(snapshot));
    } catch {
        parsed = null;
    }
    return apply(parsed);
}
}

// --- Document session bootstrap ---

/**
 * Reset the session-owned scene state that lives OUTSIDE the kernel
 * (CAD entities + their ledger records) before/after loading a document,
 * then rehydrate from the shared-protocol payload when present.
 */
export function rehydrateSessionScene(cadScene?: CadScenePayload | null): void {
    const app = useAppStore.getState();
    // Drop the previous document's CAD content through the store API so
    // the ledger mirrors the deletion. Only CAD-sourced entities go:
    // the `cad:` id prefix is the makeEntity contract, and the ledger's
    // source flag catches anything else, so other workbenches' session
    // entities are never collateral (docs/plans/2d plan F3).
    const ledger = useDocumentStore.getState().sceneObjects;
    const doomed = Object.keys(app.entities).filter((id) =>
        id.startsWith('cad:') || ledger[id]?.source === 'cad'
    );
    // Bulk swap: one write per store instead of a per-entity fan-out —
    // this runs on EVERY tab switch / document load.
    app.deleteEntities(doomed);
    if (cadScene) {
        app.addEntities(cadScene.objects.map(sceneObjectToCadEntity));
    }
    // Restore the document-scoped CAD side state (layers/blocks/views).
    restoreCadSideState(cadScene);
}

/**
 * Rebuild layers, block definitions and named views from the loaded
 * envelope. Unknown/absent fields fall back to the fresh defaults (a
 * layer-0-only store, empty defs) so old saves never leak the previous
 * document's state.
 */
function restoreCadSideState(cadScene?: CadScenePayload | null): void {
    const app = useAppStore.getState();
    // Layers: reset to default, then apply the document's set.
    Object.values(app.layers).forEach((l) => {
        if (l.id !== 'layer-0') app.deleteLayer(l.id);
    });
    app.updateLayer('layer-0', { visible: true, locked: false, color: '#ffffff' });
    const layers = (cadScene?.layers ?? []) as Array<Record<string, unknown>>;
    layers.forEach((l) => {
        if (!l || typeof l.id !== 'string' || l.id === 'layer-0') return;
        app.addLayer({
            id: l.id, name: String(l.name ?? l.id), color: String(l.color ?? '#ffffff'),
            visible: l.visible !== false, locked: !!l.locked, order: Number(l.order ?? 0),
            frozen: !!l.frozen, linetype: typeof l.linetype === 'string' ? l.linetype : undefined,
            lineweight: typeof l.lineweight === 'number' ? l.lineweight : undefined,
            transparency: typeof l.transparency === 'number' ? l.transparency : undefined,
        });
    });
    if (typeof cadScene?.activeLayerId === 'string' && app.layers[cadScene.activeLayerId]) {
        app.setActiveLayer(cadScene.activeLayerId);
    } else {
        app.setActiveLayer('layer-0');
    }
    // Block definitions: wipe the previous document's defs, re-register.
    const defs = Object.keys(useBlockStore.getState().defs);
    defs.forEach((n) => useBlockStore.getState().remove(n));
    const blocks = (cadScene?.blocks ?? []) as Array<{ name?: string }>;
    blocks.forEach((b) => {
        if (b && typeof b.name === 'string') useBlockStore.getState().define(b as never);
    });
    // Named views: reset to the document's saved set.
    const views = (cadScene?.namedViews ?? []) as Array<{ name?: string }>;
    useViewStore.getState().replaceAllViews(views.filter((v) => v && typeof v.name === 'string') as never);
    // Paper-space layouts (Faz 9): restore the layout registry + active id.
    const layoutPayload = cadScene?.layouts as { layouts?: unknown[]; activeId?: string | null } | undefined;
    useLayoutStore.getState().replaceAll(
        (layoutPayload?.layouts ?? []) as never,
        typeof layoutPayload?.activeId === 'string' ? layoutPayload.activeId : null
    );
}

// --- New (from template) ---

/**
 * Replaces the working document with a fresh document derived from the
 * selected template. createKind decides whether the document is a project
 * (.yap) or a project template (.yate) — BIM's "Create New" radio.
 */
export async function createDocumentFromTemplate(
    templateId: string,
    createKind: DocumentKind,
    docName: string
): Promise<boolean> {
    if (!(await beforeDocumentSwap())) return false;
    try {
        const projectJson = await buildProjectJsonFromTemplate(templateId, docName);
        await useBIMStore.getState().loadProject(projectJson);
        useBIMStore.getState().clearSelection();
        // A template-derived document carries no CAD content: drop the
        // previous document's session-owned entities from store + ledger.
        rehydrateSessionScene(null);
        // ...and no freeform content: the Sketch workbench starts clean too.
        useSketchStore.getState().clearDocument();
        const doc = useDocumentStore.getState();
        doc.setDocument({ kind: createKind, displayName: docName });
        doc.pushRecent({ name: docName, kind: createKind });
        notify(i18n.t('tools.notify.docCreated', { kind: createKind === 'template' ? i18n.t('tools.notify.kindTemplate') : i18n.t('tools.notify.kindProject'), name: docName }),
            'success'
        );
        return true;
    } catch (error) {
        console.error('[ProjectFileIO] Failed to create document from template:', error);
        notify(i18n.t('tools.notify.failedToCreateTheDocumentFrom'), 'error');
        return false;
    }
}

// --- Open ---

/**
 * Opens the OS Open dialog (or the in-app browser as fallback).
 * Resolves true when a document was actually loaded.
 */
export async function openDocument(): Promise<boolean> {
    if (isFileSystemAccessSupported()) {
        const handle = await pickFileToOpen();
        if (!handle) return false;
        try {
            const text = await readHandleText(handle);
            return await loadDocumentFromData(text, stripExtension(handle.name), null, handle);
        } catch (error) {
            console.error('[ProjectFileIO] Failed to open file:', error);
            notify(i18n.t('tools.notify.openFailed', { fileName: handle.name }), 'error');
            return false;
        }
    }
    // Fallback: custom in-app file browser over the virtual workspace.
    window.dispatchEvent(new CustomEvent('yap:open-file-browser', { detail: { mode: 'open' } }));
    return false;
}

/**
 * Loads serialized document contents into the kernel and registers it as
 * the working document. Shared by the native dialog, the in-app browser
 * and the "import from computer" flow.
 */
export async function loadDocumentFromData(
    text: string,
    displayName: string,
    vfsFileId: string | null = null,
    fileHandle: FileSystemFileHandle | null = null,
    skipGuard = false
): Promise<boolean> {
    // Hydrate sidecar tokens back to inline data URLs before parsing so the
    // kernel and the CAD store keep seeing plain serializable payloads.
    text = await resolveAssetRefs(text);
    let parsed: ParsedDocument | null;
    try {
        parsed = parseDocumentObject(JSON.parse(text));
    } catch {
        parsed = null;
    }
    if (!parsed) {
        notify(i18n.t('tools.notify.unsupportedFileExpectedAYapProject'), 'error');
        return false;
    }
    return applyDocumentPayload(parsed, displayName, vfsFileId, fileHandle, skipGuard);
}

/**
 * Shared load tail: kernel swap + session-owned scene rehydration + document
 * registration. File loads (loadDocumentFromData) and parked session-tab
 * restores (loadSessionSnapshot) feed it with an already-parsed payload.
 */
async function applyDocumentPayload(
    parsed: ParsedDocument,
    displayName: string,
    vfsFileId: string | null,
    fileHandle: FileSystemFileHandle | null,
    skipGuard: boolean
): Promise<boolean> {
    // BIM asks about unsaved edits when a document is actually about to be
    // replaced — after the file was chosen and parsed, not before the
    // picker. Multi-tab mode parks the outgoing document into its session
    // tab instead of prompting. Recovery flows pass skipGuard: the restore
    // prompt IS the user confirmation.
    if (!skipGuard && !(await beforeDocumentSwap())) return false;
    try {
        await useBIMStore.getState().loadProjectData(parsed.project);
        useBIMStore.getState().clearSelection();
        // CAD content is outside the kernel: swap the previous session's
        // entities for the opened document's records via the store API so
        // the ledger mirrors every change (Phase A2).
        rehydrateSessionScene(parsed.cadScene);
        // Sketch content likewise: replace the freeform document wholesale
        // (empty payload = fresh sketch document in this file).
        useSketchStore.getState().hydrateSketchDocument(parsed.sketchScene ?? {});
        const doc = useDocumentStore.getState();
        doc.setDocument({ kind: parsed.kind, displayName, fileHandle, vfsFileId });
        doc.pushRecent({ name: displayName, kind: parsed.kind, vfsFileId, fileHandle });
        notify(i18n.t('tools.notify.docOpened', { kind: parsed.kind === 'template' ? i18n.t('tools.notify.kindTemplate') : i18n.t('tools.notify.kindProject'), name: displayName }), 'success');
        return true;
    } catch (error) {
        console.error('[ProjectFileIO] Failed to load document:', error);
        notify(i18n.t('tools.notify.theFileCouldNotBeLoaded'), 'error');
        return false;
    }
}

// --- Recent documents ---

/**
 * Opens a document straight from the recents list: virtual-workspace files
 * read from IndexedDB, native files through the parked FileSystemFileHandle
 * (permission re-verified). Falls back to the regular Open dialog when the
 * stored destination is gone — BIM shows the same "file unavailable"
 * behavior by reopening the picker. Tab-activation flows pass skipGuard:
 * the outgoing document was parked before the swap.
 */
export async function openRecentDocument(recent: RecentFile, skipGuard = false): Promise<boolean> {
    if (recent.vfsFileId) {
        const entry = await virtualFs.getEntry(recent.vfsFileId);
        if (entry && entry.type === 'file' && entry.content !== undefined) {
            return loadDocumentFromData(entry.content, entry.name, entry.id, null, skipGuard);
        }
        notify(i18n.t('tools.notify.recentMissing', { fileName: recent.name }), 'warning');
        return openDocument();
    }
    if (recent.hasHandle) {
        const handle = await getRecentHandle(recent.id);
        if (handle && (await verifyReadPermission(handle))) {
            try {
                const text = await readHandleText(handle);
                return await loadDocumentFromData(text, stripExtension(handle.name), null, handle, skipGuard);
            } catch (error) {
                console.error('[ProjectFileIO] Failed to open recent file:', error);
                notify(i18n.t('tools.notify.recentOpenFailed', { fileName: recent.name }), 'error');
                return openDocument();
            }
        }
        notify(i18n.t('tools.notify.recentAccessExpired', { fileName: recent.name }), 'warning');
        return openDocument();
    }
    return openDocument();
}

// --- Save / Save As ---

/**
 * Saves the working document. Uses the existing destination when the
 * document was opened/saved before (true "Save"), otherwise falls through
 * to the Save As flow (BIM behavior for never-saved documents).
 */
export async function saveCurrentDocument(): Promise<boolean> {
    const doc = useDocumentStore.getState();
    const kind = doc.kind;

    if (doc.fileHandle) {
        try {
            const envelopeText = await serializeDocument(kind);
            await writeHandleText(doc.fileHandle, envelopeText);
        } catch (error) {
            console.error('[ProjectFileIO] Failed to save file:', error);
            notify(i18n.t('tools.notify.writeFailed', { fileName: doc.fileHandle.name }), 'error');
            return false;
        }
        doc.clearDirty();
        doc.pushRecent({ name: doc.displayName, kind, fileHandle: doc.fileHandle });
        notify(i18n.t('tools.notify.saved', { fileName: doc.fileHandle.name }), 'success');
        return true;
    }

    if (doc.vfsFileId) {
        const entry = await virtualFs.getEntry(doc.vfsFileId);
        if (entry && entry.type === 'file') {
            const envelopeText = await serializeDocument(kind);
            await virtualFs.writeFile(entry.parentId, entry.name, envelopeText, kind);
            doc.clearDirty();
            doc.pushRecent({ name: doc.displayName, kind, vfsFileId: entry.id });
            notify(i18n.t('tools.notify.savedToWorkspace', { fileName: entry.name }), 'success');
            return true;
        }
    }

    return saveDocumentAs();
}

/** Prompts for a destination (native Save dialog or in-app browser). */
export async function saveDocumentAs(): Promise<boolean> {
    const doc = useDocumentStore.getState();
    const kind = doc.kind;
    const suggestedName = `${doc.displayName || bimKernel.getProject().name}.${
        kind === 'template' ? TEMPLATE_FILE_EXTENSION : PROJECT_FILE_EXTENSION
    }`;

    if (isFileSystemAccessSupported()) {
        const handle = await pickFileToSave(suggestedName);
        if (!handle) return false;
        try {
            const envelopeText = await serializeDocument(kind);
            await writeHandleText(handle, envelopeText);
        } catch (error) {
            console.error('[ProjectFileIO] Failed to save file:', error);
            notify(i18n.t('tools.notify.writeFailed', { fileName: handle.name }), 'error');
            return false;
        }
        doc.setDocument({ kind, displayName: stripExtension(handle.name), fileHandle: handle });
        doc.pushRecent({ name: stripExtension(handle.name), kind, fileHandle: handle });
        notify(i18n.t('tools.notify.saved', { fileName: handle.name }), 'success');
        return true;
    }

    // Fallback: queue the envelope and let the in-app browser dialog
    // write it into the virtual workspace (or download it).
    const envelopeText = await serializeDocument(kind);
    doc.setPendingSave({ envelopeText, suggestedName, kind });
    window.dispatchEvent(new CustomEvent('yap:open-file-browser', { detail: { mode: 'save' } }));
    return true;
}

// --- Template files (.yate) ---

/**
 * Exports a custom template from the library as a .yate file — native
 * Save dialog when available, browser download otherwise.
 */
export async function exportTemplateFile(templateId: string, templateName: string): Promise<void> {
    const projectJson = getCustomTemplateJson(templateId);
    if (!projectJson) {
        notify(i18n.t('tools.notify.templateNotFoundInTheLibrary'), 'error');
        return;
    }
    const fileName = `${templateName}.${TEMPLATE_FILE_EXTENSION}`;
    const envelope = buildEnvelope('template', projectJson);
    await externalizeAssets(envelope);
    const envelopeText = JSON.stringify(envelope, null, 2);

    if (isFileSystemAccessSupported()) {
        const handle = await pickFileToSave(fileName);
        if (!handle) return;
        try {
            await writeHandleText(handle, envelopeText);
            notify(i18n.t('tools.notify.templateExported', { fileName: handle.name }), 'success');
        } catch (error) {
            console.error('[ProjectFileIO] Failed to export template:', error);
            notify(i18n.t('tools.notify.writeFailed', { fileName: handle.name }), 'error');
        }
        return;
    }
    downloadTextFile(fileName, envelopeText);
    notify(i18n.t('tools.notify.templateExported', { fileName: fileName }), 'success');
}
